import React, { useState, useEffect } from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { SparkLineChart } from '@mui/x-charts/SparkLineChart';
import { areaElementClasses, lineElementClasses } from '@mui/x-charts/LineChart';
import { chartsAxisHighlightClasses } from '@mui/x-charts/ChartsAxisHighlight';

const ObrasSparkline = ({ obras = [] }) => {
  const [dayIndex, setDayIndex] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [days, setDays] = useState([]);

  useEffect(() => {
    // Generar datos de los últimos 30 días
    const today = new Date();
    const last30Days = [];
    const obrasCount = [];
    
    for (let i = 29; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      
      const dateStr = date.toISOString().split('T')[0];
      const dayLabel = date.toLocaleDateString('es-ES', { 
        day: 'numeric', 
        month: 'short' 
      });
      
      // Contar obras creadas en esta fecha
      const obrasEnFecha = obras.filter(obra => {
        if (!obra.FechaVisita) return false;
        const obraDate = new Date(obra.FechaVisita).toISOString().split('T')[0];
        return obraDate === dateStr;
      }).length;
      
      last30Days.push(dayLabel);
      obrasCount.push(obrasEnFecha);
    }
    
    setDays(last30Days);
    setChartData(obrasCount);
  }, [obras]);

  const settings = {
    data: chartData,
    baseline: 'min',
    margin: { bottom: 0, top: 5, left: 4, right: 0 },
    xAxis: { 
      id: 'day-axis', 
      data: days,
    },
    yAxis: {
      domainLimit: (_, maxValue) => ({
        min: -maxValue / 6,
        max: maxValue,
      }),
    },
    sx: {
      [`& .${areaElementClasses.root}`]: { opacity: 0.2 },
      [`& .${lineElementClasses.root}`]: { strokeWidth: 3 },
      [`& .${chartsAxisHighlightClasses.root}`]: {
        stroke: 'rgb(147, 51, 234)', // purple-600
        strokeDasharray: 'none',
        strokeWidth: 2,
      },
    },
    slotProps: {
      lineHighlight: { r: 4 },
    },
    clipAreaOffset: { top: 2, bottom: 2 },
    axisHighlight: { x: 'line' },
    showTooltip: false,
    // disableAxisListener: false,
  };

  const totalObras = chartData.reduce((sum, count) => sum + count, 0);

  return (
    <Box
      onKeyDown={(event) => {
        switch (event.key) {
          case 'ArrowLeft':
            setDayIndex((p) =>
              p === null ? days.length - 1 : (days.length + p - 1) % days.length,
            );
            break;
          case 'ArrowRight':
            setDayIndex((p) => (p === null ? 0 : (p + 1) % days.length));
            break;
          default:
        }
      }}
      onFocus={() => {
        setDayIndex((p) => (p === null ? 0 : p));
      }}
      role="button"
      aria-label="Mostrando obras creadas en el último mes"
      tabIndex={0}
      width="100%"
      height="100%"
      display="flex"
      justifyContent="center"
      alignItems="center"
    >
      <Stack direction="column" width="100%">
        {/* <Typography
          sx={{
            color: 'rgb(107, 114, 128)', // gray-500
            fontWeight: 400,
            fontSize: '14px',
            pt: 1,
          }}
        >
          <ObrasIcon
            fill="rgb(147, 51, 234)" // purple-600
            width="14px"
            height="14px"
            borderRadius="4px"
            borderColor="rgb(147, 51, 234)" // purple-600
            style={{ marginRight: 8 }}
          />
          {dayIndex === null ? 'Obras creadas (último mes)' : `${days[dayIndex]} - ${chartData[dayIndex]} obras`}
        </Typography> */}

        <Typography
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: 'rgb(107, 114, 128)', // gray-500
                fontWeight: 400,
                fontSize: '14px',
                pt: 1,
                fontFamily: "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
            }}
            >
            {dayIndex === null
                ? 'Obras creadas (último mes)'
                : `Fecha: ${days[dayIndex]}`}
        </Typography>

        <Stack
          direction="row"
          paddingTop={2}
          justifyContent="space-between"
          alignItems="flex-end"
          sx={{ borderBottom: 'solid 2px rgba(147, 51, 234, 0.2)' }} // purple-600 with opacity
        >
          <Typography sx={{ 
            fontSize: { xs: '1.5rem', sm: '2rem' }, // xs: mobile, sm: tablet+
            fontWeight: 600, 
            color: '#1f2937',
            fontFamily: "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
          }}> {/* gray-800 */}
            {dayIndex === null 
              ? totalObras.toLocaleString('es-ES')
              : (chartData[dayIndex] || 0).toLocaleString('es-ES')
            }
          </Typography>

          {chartData.length > 0 && (
            <Box
              onMouseMove={(event) => {
                const box = event.currentTarget.getBoundingClientRect();
                const chartWidth = box.width;
                const x = event.clientX - box.left;
                const chartWidthCalc = chartWidth - settings.margin.left - settings.margin.right;
                const index = Math.floor((x - settings.margin.left) / chartWidthCalc * days.length);
                
                if (index >= 0 && index < days.length) {
                  setDayIndex(index);
                }
              }}
              onMouseLeave={() => {
                setDayIndex(null);
              }}
              sx={{ 
                cursor: 'crosshair', 
                position: 'relative',
                width: '80%',
                height: 70,
              }}
            >
              {/* Líneas verticales de fondo */}
              <svg 
                width="100%" 
                height={70}
                preserveAspectRatio="none"
                style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}
              >
                {days.map((_, index) => {
                  const x = `${(index / (days.length - 1)) * 100}%`;
                  return (
                    <line
                      key={index}
                      x1={x}
                      y1={settings.margin.top}
                      x2={x}
                      y2={70}
                      stroke="rgba(147, 51, 234, 0.15)"
                      strokeWidth={1.5}
                      vectorEffect="non-scaling-stroke"
                    />
                  );
                })}
              </svg>
              
              <SparkLineChart
                height={70}
                area
                showHighlight
                showTooltip={false}
                color="rgb(147, 51, 234)" // purple-600
                highlightedItem={
                  dayIndex === null
                    ? undefined
                    : { seriesId: 'auto-generated-id-0', dataIndex: dayIndex }
                }
                {...settings}
              />
            </Box>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

// function ObrasIcon(props) {
//   return (
//     <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
//       <title>Obras</title>
//       <g>
//         <path
//           d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
//           fill="none"
//           stroke="currentColor"
//           strokeWidth={2}
//           strokeLinecap="round"
//           strokeLinejoin="round"
//         />
//       </g>
//     </svg>
//   );
// }

export default ObrasSparkline;
